import React, { useState } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { useTrainingAuth } from '../context/TrainingAuthContext';
import trainingAuthService from '../services/trainingAuthService';

const ROLES = [
  { value: 'training_coordinator', label: 'Training Coordinator' },
  { value: 'training_document_controller', label: 'Training Document Controller' },
  { value: 'training_reviewer', label: 'Training Reviewer' },
  { value: 'training_admin', label: 'Training Admin' },
];

const TrainingLogin = () => {
  const navigate = useNavigate();
  const { trainingLogin } = useTrainingAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', role: '' });

  const handleInputChange = (e) => {
    if (error) setError('');
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password || !formData.role) {
      setError('Please fill in all fields');
      return;
    }
    setLoading(true);
    try {
      const data = await trainingAuthService.login(formData.email, formData.password, formData.role);
      trainingLogin(data.access_token, data.refresh_token, data.user);

      const role = data.user.role;
      if (['training_coordinator', 'training_document_controller'].includes(role)) {
        navigate('/training/coordinator');
      } else {
        navigate('/training/reviewer');
      }
    } catch (err) {
      setError(err.error || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container className="mt-5">
      <Row className="justify-content-center">
        <Col md={8} lg={5}>
          <Card className="shadow-lg">
            <Card.Header className="text-white text-center py-3"
              style={{ background: 'linear-gradient(135deg, #198754 0%, #146c43 100%)' }}>
              <h3 className="mb-0">
                <i className="fas fa-sign-in-alt me-2"></i>
                Login
              </h3>
              <small>Equipment Inspection Training System</small>
            </Card.Header>

            <Card.Body className="p-4">
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  <i className="fas fa-exclamation-circle me-2"></i>{error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-envelope me-2"></i>
                    Email Address <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    placeholder="Enter your email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-lock me-2"></i>
                    Password <span className="text-danger">*</span>
                  </Form.Label>
                  <div className="position-relative">
                    <Form.Control
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={handleInputChange}
                      required
                    />
                    <Button
                      variant="link"
                      className="position-absolute end-0 top-0 text-secondary"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ textDecoration: 'none' }}
                      type="button"
                    >
                      <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </Button>
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>
                    <i className="fas fa-user-tag me-2"></i>
                    Select Your Role <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">-- Select Role --</option>
                    {ROLES.map(r => (
                      <option key={r.value} value={r.value}>{r.label}</option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    <i className="fas fa-info-circle me-1"></i>
                    All three fields (Email, Password, and Role) must be correct
                  </Form.Text>
                </Form.Group>

                <Button
                  variant="success"
                  type="submit"
                  className="w-100 py-2 fw-bold"
                  disabled={loading}
                >
                  {loading ? (
                    <><span className="spinner-border spinner-border-sm me-2"></span>Logging in...</>
                  ) : (
                    <><i className="fas fa-sign-in-alt me-2"></i>Login</>
                  )}
                </Button>
              </Form>

              <div className="text-center mt-3">
                <small className="text-muted">
                  Forgot your password? <a href="mailto:admin@indoveritas.com" className="text-success">Contact Admin</a>
                </small>
              </div>
            </Card.Body>

            <Card.Footer className="text-center py-3">
              <small>
                Don't have an account?{' '}
                <a href="/training-register" className="text-success fw-bold">Register here</a>
              </small>
            </Card.Footer>
          </Card>

          <div className="text-center mt-3">
            <Button variant="outline-secondary" onClick={() => navigate('/')}>
              <i className="fas fa-home me-2"></i>Back to Home
            </Button>
          </div>

          {/* Role Information */}
          <Card className="mt-3 border-success">
            <Card.Body className="py-3">
              <h6 className="text-success mb-2">
                <i className="fas fa-info-circle me-2"></i>Role Information
              </h6>
              <div style={{ fontSize: '0.85rem' }}>
                <div><strong>Training Coordinator / Doc Controller:</strong> Fill and submit certificate forms</div>
                <div><strong>Training Reviewer / Admin:</strong> Review, approve or send back for corrections</div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default TrainingLogin;
